import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useSectionTracking } from "./AnalyticsProvider";

const muzoFacts = [
  { title: "The Most Coveted Origin on Earth", text: "Muzo emeralds have been the most sought-after specimens in the gemological world for over 500 years. The local Muzo Indians withheld the exact mine location from the Spanish conquistadors for twenty years. Today, 'Muzo origin' on a Gübelin certificate is the single most valuable provenance designation in the emerald world — a mark of exclusivity that cannot be manufactured or replicated." },
  { title: "Formed 30–38 Million Years Ago", text: "Colombian emeralds were created through hydrothermal activity — hot, briny fluids that leached chemical elements from black shale in the Cordillera Oriental of the Andes. This unique genesis gives Muzo stones their legendary warm, slightly bluish-green colour that no other deposit can replicate. Every Muzo emerald is a fragment of geological time that predates humanity." },
  { title: "Rarer Than Diamonds", text: "Beryllium and chromium — the two elements essential to creating an emerald — are separated by most geological processes on Earth. According to Gübelin's Gemological Profile, their co-occurrence is an unusual event even across geologic dimensions of time and space. At 1,055 carats, Bridgette is a singular event in Earth's history." },
];

const famousEmeralds = [
  { name: "The Aga Khan Emerald", weight: "37.00 ct", year: "2024", price: "~$8.83M", detail: "Cartier platinum brooch, 1960. Christie's Geneva, Lot 100, Nov 2024. Guinness World Record. SSEF & Gübelin certified.", img: "/media/photos/aga_khan_emerald.jpg", source: "Image: Christie's" },
  { name: "The Rockefeller Emerald", weight: "18.04 ct", year: "2017", price: "$5.51M", detail: "Octagonal step-cut. Acquired by J.D. Rockefeller Jr., 1930. Remounted by Raymond Yard, 1948. AGL certified.", img: "/media/photos/rockefeller_emerald.jpg", source: "Image: Christie's" },
  { name: "Elizabeth Taylor Bulgari", weight: "23.44 ct", year: "2011", price: "$6.58M", detail: "Colombian pendant/brooch by Bulgari. Gift from Richard Burton. Part of the $115.9M Elizabeth Taylor collection at Christie's NY.", img: "/media/photos/elizabeth_taylor_emerald.jpg", source: "Image: AP Photo" },
  { name: "Duke of Devonshire", weight: "1,383.93 ct", year: "c.1831", price: "Priceless", detail: "Uncut Muzo hexagonal crystal. Gifted by Emperor Pedro I of Brazil. Displayed at 1851 Great Exhibition. Natural History Museum, London.", img: "/media/photos/duke_devonshire.jpg", source: "Photo: Wikimedia, CC BY-SA 4.0" },
];

const rawSpecimens = [
  { name: "The Bridgette", weight: "1,055 ct", origin: "Muzo, Colombia", value: "Gübelin Certified", status: "Available", detail: "The only raw emerald in 150 years to receive a Gübelin Premium Gemological Profile. There is no second one.", img: "/media/photos/new_1.jpg", highlight: true },
  { name: "La Gloria", weight: "887 ct", origin: "Muzo, Colombia", value: "Est. $4–5M", status: "Unsold", detail: "Fourth largest known rough Colombian. Marcial de Gomar Collection. Failed to sell at Guernsey's, 2017. Bridgette is 19% larger.", img: "/media/photos/la_gloria.jpg" },
  { name: "Duke of Devonshire", weight: "1,383.93 ct", origin: "Muzo, Colombia", value: "Priceless", status: "Museum", detail: "Gifted by Emperor Pedro I of Brazil, c.1831. Displayed at the 1851 Great Exhibition. Natural History Museum, London.", img: "/media/photos/duke_devonshire.jpg" },
];

function Carousel3D({ items, renderCard }) {
  const [active, setActive] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const timerRef = useRef(null);
  const total = items.length;

  const goTo = useCallback((i) => {
    setActive(((i % total) + total) % total);
    setAutoPlay(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setAutoPlay(true), 10000);
  }, [total]);

  useEffect(() => {
    if (!autoPlay) return;
    const iv = setInterval(() => setActive(c => (c + 1) % total), 5000);
    return () => clearInterval(iv);
  }, [autoPlay, total]);

  const getStyle = (index) => {
    const diff = ((index - active) % total + total) % total;
    if (diff === 0) return { x: "0%", scale: 1, zIndex: 30, opacity: 1, rotateY: 0 };
    if (diff === 1) return { x: "55%", scale: 0.78, zIndex: 20, opacity: 0.5, rotateY: -12 };
    if (diff === total - 1) return { x: "-55%", scale: 0.78, zIndex: 20, opacity: 0.5, rotateY: 12 };
    if (diff === 2) return { x: "95%", scale: 0.6, zIndex: 10, opacity: 0.25, rotateY: -20 };
    if (diff === total - 2) return { x: "-95%", scale: 0.6, zIndex: 10, opacity: 0.25, rotateY: 20 };
    return { x: "0%", scale: 0.4, zIndex: 0, opacity: 0, rotateY: 0 };
  };

  return (
    <div className="relative" style={{ perspective: "1400px" }}>
      <div className="relative h-[520px] sm:h-[560px] lg:h-[600px] overflow-hidden">
        {items.map((item, i) => {
          const s = getStyle(i);
          return (
            <motion.div
              key={i}
              animate={{ x: s.x, scale: s.scale, opacity: s.opacity, rotateY: s.rotateY, zIndex: s.zIndex }}
              transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
              className="absolute top-0 left-1/2 w-[380px] sm:w-[440px] lg:w-[500px] cursor-pointer"
              style={{ marginLeft: "-190px", transformStyle: "preserve-3d" }}
              onClick={() => goTo(i)}
            >
              {renderCard(item, i === active)}
            </motion.div>
          );
        })}
      </div>

      <button onClick={() => goTo(active - 1)} className="absolute left-2 sm:left-6 top-[40%] -translate-y-1/2 z-40 w-12 h-12 flex items-center justify-center bg-[#111110]/80 border border-[#C5A059]/15 text-[#B8B3AA]/70 hover:text-[#C5A059] transition-colors">
        <ChevronLeft size={22} strokeWidth={1.5} />
      </button>
      <button onClick={() => goTo(active + 1)} className="absolute right-2 sm:right-6 top-[40%] -translate-y-1/2 z-40 w-12 h-12 flex items-center justify-center bg-[#111110]/80 border border-[#C5A059]/15 text-[#B8B3AA]/70 hover:text-[#C5A059] transition-colors">
        <ChevronRight size={22} strokeWidth={1.5} />
      </button>

      <div className="flex justify-center gap-2 mt-4">
        {items.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} className={`h-1.5 transition-all ${i === active ? "bg-[#C5A059] w-7" : "bg-[#B8B3AA]/15 w-2.5"}`} />
        ))}
      </div>
    </div>
  );
}

export default function EmeraldEducation() {
  const sectionRef = useSectionTracking("emerald_education");
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-80px" });

  const renderFamousCard = (em, isActive) => (
    <div className={`bg-[#1A1918] border transition-all duration-500 ${isActive ? "border-[#C5A059]/30 shadow-2xl shadow-black/50" : "border-[#C5A059]/5"}`}>
      <div className="h-[280px] sm:h-[300px] lg:h-[320px] overflow-hidden">
        <img src={em.img} alt={em.name} className="w-full h-full object-cover object-center" />
      </div>
      <div className="p-5 lg:p-6 bg-[#1A1918]">
        <div className="font-serif text-2xl lg:text-3xl gold-gradient-text mb-1">{em.price}</div>
        <h4 className="font-serif text-lg text-[#F4F0EA] mb-1">{em.name}</h4>
        <p className="text-base text-[#B8B3AA]/65">{em.weight} — {em.year}</p>
        {isActive && (
          <div className="pt-3 mt-3 border-t border-[#C5A059]/10">
            <p className="text-sm text-[#B8B3AA]/75 leading-relaxed">{em.detail}</p>
            <p className="text-xs text-[#B8B3AA]/35 mt-2">{em.source}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderRawCard = (spec, isActive) => (
    <div className={`border transition-all duration-500 ${spec.highlight ? "bg-[#1C1A15] border-[#C5A059]/25" : "bg-[#1A1918] border-[#C5A059]/5"} ${isActive ? "shadow-2xl shadow-black/50 ring-1 ring-[#C5A059]/20" : ""}`}>
      <div className="h-[280px] sm:h-[300px] lg:h-[320px] overflow-hidden">
        <img src={spec.img} alt={spec.name} className="w-full h-full object-cover object-center" />
      </div>
      <div className={`p-5 lg:p-6 ${spec.highlight ? "bg-[#1C1A15]" : "bg-[#1A1918]"}`}>
        <div className={`font-serif text-2xl lg:text-3xl mb-1 ${spec.highlight ? "gold-gradient-text" : "text-[#F4F0EA]/70"}`}>{spec.value}</div>
        <h4 className={`font-serif text-lg mb-1 ${spec.highlight ? "text-[#C5A059]" : "text-[#F4F0EA]/90"}`}>{spec.name}</h4>
        <p className="text-base text-[#B8B3AA]/65">{spec.weight} — {spec.origin}</p>
        {isActive && (
          <div className="pt-3 mt-3 border-t border-[#C5A059]/10">
            <p className="text-base text-[#B8B3AA]/75">{spec.status}</p>
            <p className="text-sm text-[#B8B3AA]/75 leading-relaxed mt-1">{spec.detail}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <section id="education" ref={sectionRef} className="relative py-12 lg:py-18 overflow-hidden" data-testid="education-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12" ref={animRef}>

        <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 1 }} className="mb-8 text-center">
          <blockquote className="font-serif text-xl sm:text-2xl lg:text-3xl italic text-[#C5A059]/50 max-w-3xl mx-auto leading-relaxed">
            "Wise words are rarer than emeralds"
          </blockquote>
          <p className="text-xs text-[#B8B3AA]/25 mt-3">— The Instruction of Ptah-Hotep, c. 2400 BC (cited in the Gübelin Gemological Profile)</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} className="mb-8 lg:mb-12">
          <p className="overline mb-4">The Muzo Advantage</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter mb-10">
            Why <span className="italic text-[#C5A059]">Muzo</span> Commands the World's Highest Prices
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#C5A059]/10">
            <div className="lg:col-span-4 relative overflow-hidden">
              <img src="https://images.unsplash.com/photo-1592830469504-43d8447891f2?w=800&h=900&fit=crop" alt="Colombian highlands" className="w-full h-full min-h-[220px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111110] via-transparent to-transparent" />
            </div>
            <div className="lg:col-span-8">
              {muzoFacts.map((fact, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                  className="p-5 lg:p-7 border-b border-[#C5A059]/10 last:border-b-0">
                  <h3 className="font-serif text-lg text-[#F4F0EA] mb-2">{fact.title}</h3>
                  <p className="text-base text-[#B8B3AA]/70 leading-relaxed font-light">{fact.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.3 }} className="mb-10 lg:mb-14">
          <p className="overline mb-4">Historical Context</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter mb-8">
            Famous Emeralds <span className="italic text-[#C5A059]">in History</span>
          </h2>
          <Carousel3D items={famousEmeralds} renderCard={renderFamousCard} />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.4 }}>
          <p className="overline mb-4">The Collector's Case</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter mb-8">
            Bridgette's <span className="italic text-[#C5A059]">Competitive Class</span>
          </h2>
          <Carousel3D items={rawSpecimens} renderCard={renderRawCard} />
        </motion.div>
      </div>
    </section>
  );
}
